import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";

import { NewWalletAction } from "../../actions";
import { Label, Message, Radio, SelectionCard } from "../../common";
import { CURRENCY_MAP, DEFAULT_CRYPTO_CURRENCY } from "../../../constants";
import { useUpdateInvoiceMutation } from "../../../store/services/invoices";
import { useTriggerToastOnRTKError } from "../../../hooks/useTriggerToastOnRTKError";
import { selectInvoice } from "../../../store/reducers/invoice";
import { selectWallets } from "../../../store/reducers/wallets";
import { UIStatus } from "../../../types/GlobalTypes";
import { CurrencyType } from "../../../types/InvoiceTypes";
import { Chain, Wallet } from "../../../types/WalletTypes";
import { useAllowedChains } from "../../../hooks/useAllowedChains";

type Props = {
  onSelect?: (wallet: Wallet) => void;
};

const walletTypeName = {
  [CurrencyType.CRYPTO]: "wallet",
  [CurrencyType.FIAT]: "bank account",
};

const walletTypeMessage = {
  [CurrencyType.CRYPTO]:
    "Make sure you selected the correct network and wallet address. After approving invoice you can't change it.",
  [CurrencyType.FIAT]: undefined,
};

const getCardTitle = (wallet: Wallet) => {
  return wallet.address
    ? `${wallet.chain} • ${wallet.name}`
    : `${wallet.bank_name || wallet.bank_code || ""} - ${wallet.name}`;
};

const getCardValue = (wallet: Wallet) => {
  return wallet.address ? wallet.address! : wallet.account_number!;
};

const isOptionDisabled = (wallet: Wallet, supportedChains?: Chain[]) => {
  return !supportedChains?.includes(wallet.chain);
};

export const InvoiceWalletList: FC<Props> = ({ onSelect }) => {
  const invoice = useSelector(selectInvoice);
  const walletList = useSelector(selectWallets);
  const [updateInvoice, { isError, error }] = useUpdateInvoiceMutation();
  useTriggerToastOnRTKError(isError, error);

  const outgoingCurrency =
    invoice?.outgoing_currency || DEFAULT_CRYPTO_CURRENCY;

  const outgoingCurrencyType = CURRENCY_MAP[outgoingCurrency].type;
  const allowedChains = useAllowedChains(outgoingCurrency);

  const allowedWallets = useMemo(
    () => walletList.filter(({ chain }) => allowedChains.includes(chain!)),
    [walletList, allowedChains]
    // () =>
    //   walletList.filter(({ chain }) =>
    //     CURRENCY_MAP[outgoingCurrency].supportedChains?.includes(chain!)
    //   ),
    // [walletList, outgoingCurrency]
  );

  if (!invoice) {
    return <></>;
  }

  const { id, outgoing_payment_details } = invoice;

  const onSelectWallet = async (wallet: Wallet) => {
    await updateInvoice({
      id,
      outgoing_payment_details: {
        wallet_id: wallet.id,
      },
    });

    onSelect && onSelect(wallet);
  };

  return (
    <Label
      label={`Select ${walletTypeName[outgoingCurrencyType]}`}
      labelClassName="!text-base uppercase"
      valueClassName="flex flex-col gap-4"
    >
      {walletTypeMessage[outgoingCurrencyType] && (
        <Message
          className=""
          type={UIStatus.WARNING}
          text={walletTypeMessage[outgoingCurrencyType]}
        />
      )}

      <div className="flex flex-col gap-y-2 basis-[528px] max-w-[528px]">
        <Radio
          name="wallet"
          className=""
          onChange={(e) => {
            const wallet = allowedWallets.find((w) => {
              const [chain, address_account] = e.target.value.split("-");
              return (
                w.chain === chain &&
                (address_account === w.address ||
                  address_account === w.account_number)
              );
            });

            if (!wallet) {
              return;
            }

            onSelectWallet(wallet);
          }}
          containerClassName="gap-4"
          options={allowedWallets.map((wallet) => ({
            value: `${wallet.chain}-${wallet.address || wallet.account_number}`,
            label: (
              <div className="flex flex-col py-2 gap-y-2 border-t border-violet-50">
                <div>{getCardTitle(wallet)}</div>

                <div className="theme-text-secondary break-all">
                  {getCardValue(wallet)}
                </div>
              </div>
            ),
            disabled: isOptionDisabled(
              wallet,
              CURRENCY_MAP[outgoingCurrency].supportedChains
            ),
          }))}
          orientation="vertical"
          checked={`${outgoing_payment_details?.chain}-${outgoing_payment_details?.address || outgoing_payment_details?.account_number}`}
        />

        <NewWalletAction
          callback={(wallet: any) => onSelectWallet(wallet)}
          currencyType={outgoingCurrencyType}
        >
          {(action) => (
            <SelectionCard
              icon="Add"
              title={`Add new ${walletTypeName[outgoingCurrencyType] || "wallet"}`}
              value={`To get payments in ${CURRENCY_MAP[outgoingCurrency].code}`}
              onClick={action}
            />
          )}
        </NewWalletAction>
      </div>
    </Label>
  );
};
